import { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { RELATIONSHIP_TYPES } from '../../api/socrInventory'

const ClientRelationshipsSection = ({
  selectedId,
  relationships = [],
  peerOptions = [],
  clientLabelById,
  saving = false,
  onAdd,
  onRemove
}) => {
  const [relPeerId, setRelPeerId] = useState('')
  const [relType, setRelType] = useState('shared_codebase')
  const [relNotes, setRelNotes] = useState('')

  const handleAdd = async () => {
    if (!relPeerId) return
    await onAdd?.({
      clientIdB: relPeerId,
      relationshipType: relType,
      notes: relNotes.trim() || null
    })
    setRelPeerId('')
    setRelNotes('')
  }

  return (
    <Accordion
      defaultExpanded={false}
      disableGutters
      sx={{
        borderRadius: '8px !important',
        border: `1px solid ${alpha('#fff', 0.1)}`,
        backgroundColor: alpha('#000', 0.18),
        '&:before': { display: 'none' }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: alpha('#fff', 0.7) }} />}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>
            Relationships
            {relationships.length ? ` · ${relationships.length}` : ''}
          </Typography>
          <Typography variant='caption' sx={{ color: alpha('#fff', 0.65), fontWeight: 600 }}>
            Rare path — shared_codebase expands repos / DT / Jira via the peer client.
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>
          {relationships.length
            ? relationships.map((rel) => {
              const peerId = rel.clientIdA === selectedId ? rel.clientIdB : rel.clientIdA
              const typeLabel =
                RELATIONSHIP_TYPES.find((t) => t.value === rel.relationshipType)?.label ||
                rel.relationshipType
              return (
                <Stack
                  key={rel.id}
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    border: `1px solid ${alpha('#fff', 0.08)}`
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }} noWrap>
                      {clientLabelById?.get?.(peerId) || peerId}
                    </Typography>
                    <Typography variant='caption' sx={{ color: alpha('#fff', 0.5) }}>
                      {typeLabel}{rel.notes ? ` · ${rel.notes}` : ''}
                    </Typography>
                  </Box>
                  <IconButton
                    size='small'
                    disabled={saving}
                    onClick={() => onRemove?.(rel.id)}
                    title='Remove relationship'
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Stack>
              )
            })
            : (
              <Typography variant='caption' sx={{ color: alpha('#fff', 0.45) }}>
                No relationships yet
              </Typography>
              )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              select
              size='small'
              label='Peer client'
              value={relPeerId}
              onChange={(e) => setRelPeerId(e.target.value)}
              sx={{ minWidth: { sm: 200 }, flex: 1 }}
            >
              {peerOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size='small'
              label='Type'
              value={relType}
              onChange={(e) => setRelType(e.target.value)}
              sx={{ minWidth: { sm: 180 } }}
            >
              {RELATIONSHIP_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            size='small'
            label='Notes (optional)'
            value={relNotes}
            onChange={(e) => setRelNotes(e.target.value)}
            fullWidth
          />
          <Box>
            <Button
              size='small'
              variant='contained'
              disabled={!relPeerId || saving}
              onClick={handleAdd}
            >
              {saving ? 'Working…' : 'Add relationship'}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export { ClientRelationshipsSection }
